import { useState, useCallback, useEffect, useRef, forwardRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, Popover, ActionList, Spinner, Thumbnail } from '@shopify/polaris';
import {
  SearchIcon,
  HeartIcon,
  CartIcon,
  PersonIcon,
  ExitIcon,
  MenuIcon,
  ClockIcon,
  DeleteIcon,
} from '@shopify/polaris-icons';
import { useAuthStore } from '../../store/authStore';
import { useAuth } from '../../hooks/useAuth';
import { useShortlistStore } from '../../store/shortlistStore';
import { useInquiryStore } from '../../store/inquiryStore';
import { useProductSearch } from '../../hooks/useProductSearch';

interface HeaderBarProps {
  isHidden: boolean;
  onSearchOpen: () => void;
  onMobileMenuOpen: () => void;
}

export const HeaderBar = forwardRef<HTMLDivElement, HeaderBarProps>(
  function HeaderBar({ isHidden, onSearchOpen, onMobileMenuOpen }, ref) {
    const navigate = useNavigate();
    const { user, isAuthenticated } = useAuthStore();
    const { logout } = useAuth();
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const [searchFocused, setSearchFocused] = useState(false);
    const searchContainerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const shortlistCount = useShortlistStore((s) => s.getCount());
    const inquiryBasketCount = useInquiryStore((s) => s.getItemCount());

    const {
      query,
      results,
      loading,
      recentSearches,
      handleQueryChange,
      saveRecentSearch,
      clearRecentSearches,
      loadRecentSearches,
      reset,
      setQuery,
      searchProducts,
    } = useProductSearch();

    // Load recent searches when input is focused
    useEffect(() => {
      if (searchFocused) loadRecentSearches();
    }, [searchFocused, loadRecentSearches]);

    // Close dropdown on click outside
    useEffect(() => {
      const handleClickOutside = (e: MouseEvent) => {
        if (searchContainerRef.current && !searchContainerRef.current.contains(e.target as Node)) {
          setSearchFocused(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Close dropdown on Escape
    const handleSearchKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && query.trim()) {
          saveRecentSearch(query);
          setSearchFocused(false);
          navigate(`/search?q=${encodeURIComponent(query)}`);
        } else if (e.key === 'Escape') {
          setSearchFocused(false);
          inputRef.current?.blur();
        }
      },
      [query, saveRecentSearch, navigate]
    );

    const handleProductClick = useCallback(
      (slug: string) => {
        if (query.trim()) saveRecentSearch(query);
        setSearchFocused(false);
        reset();
        navigate(`/products/${slug}`);
      },
      [query, saveRecentSearch, navigate, reset]
    );

    const handleRecentClick = useCallback(
      (term: string) => {
        setQuery(term);
        searchProducts(term);
      },
      [setQuery, searchProducts]
    );

    const handleViewAll = useCallback(() => {
      if (query.trim()) {
        saveRecentSearch(query);
        setSearchFocused(false);
        navigate(`/search?q=${encodeURIComponent(query)}`);
      }
    }, [query, saveRecentSearch, navigate]);

    const handleLogout = useCallback(() => {
      logout();
      setUserMenuOpen(false);
    }, [logout]);

    const showDropdown = searchFocused && (query.length > 0 || recentSearches.length > 0);

    const userMenuActivator = (
      <button
        className="header-icon-btn"
        onClick={() => setUserMenuOpen((o) => !o)}
        aria-label="User menu"
      >
        <Icon source={PersonIcon} />
      </button>
    );

    return (
      <header
        ref={ref}
        className={`header-bar ${isHidden ? 'header-bar--hidden' : ''}`}
      >
        <div className="header-container">
          {/* Left: Logo */}
          <div className="header-left">
            <button
              className="header-hamburger"
              onClick={onMobileMenuOpen}
              aria-label="Open menu"
            >
              <Icon source={MenuIcon} />
            </button>
            <div
              className="header-logo"
              onClick={() => navigate('/')}
              role="link"
              aria-label="Gift Sangam - Home"
            >
              <img
                src="/GS_logo_no_tagline.png"
                alt="Gift Sangam"
                className="header-logo-img"
              />
            </div>
          </div>

          {/* Center: Inline Search */}
          <div className="header-center" ref={searchContainerRef}>
            <div className="header-search-wrapper">
              <div className="header-search-bar">
                <span className="header-search-icon"><Icon source={SearchIcon} /></span>
                <input
                  ref={inputRef}
                  type="text"
                  className="header-search-input"
                  placeholder="Search products..."
                  value={query}
                  onChange={(e) => handleQueryChange(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onKeyDown={handleSearchKeyDown}
                  autoComplete="off"
                />
                {query && (
                  <button
                    className="header-search-clear"
                    onClick={() => { handleQueryChange(''); inputRef.current?.focus(); }}
                    aria-label="Clear search"
                  >
                    &times;
                  </button>
                )}
              </div>

              {/* Search Dropdown */}
              {showDropdown && (
                <div className="search-dropdown">
                  {/* Loading */}
                  {loading && (
                    <div className="search-dropdown-loading">
                      <Spinner size="small" />
                      <span>Searching...</span>
                    </div>
                  )}

                  {/* Results */}
                  {!loading && query && results.length > 0 && (
                    <div className="search-dropdown-section">
                      <div className="search-dropdown-heading">
                        Products ({results.length})
                      </div>
                      {results.map((product) => (
                        <button
                          key={product.id}
                          className="search-dropdown-item"
                          onClick={() => handleProductClick(product.slug)}
                        >
                          <Thumbnail
                            source={product.primaryImage || 'https://placehold.co/40x40/e2e8f0/64748b?text=No+Img'}
                            alt={product.name}
                            size="small"
                          />
                          <div className="search-dropdown-item-info">
                            <span className="search-dropdown-item-name">{product.name}</span>
                            <span className="search-dropdown-item-meta">
                              {product.categoryName}
                              {product.startingPrice && ` · From ₹${product.startingPrice.toLocaleString()}`}
                            </span>
                          </div>
                        </button>
                      ))}
                      <button className="search-dropdown-viewall" onClick={handleViewAll}>
                        View all results for "{query}"
                      </button>
                    </div>
                  )}

                  {/* No Results */}
                  {!loading && query && results.length === 0 && (
                    <div className="search-dropdown-empty">
                      No products found for "{query}"
                    </div>
                  )}

                  {/* Recent Searches (when query is empty) */}
                  {!query && recentSearches.length > 0 && (
                    <div className="search-dropdown-section">
                      <div className="search-dropdown-heading">
                        <span>Recent Searches</span>
                        <button className="search-dropdown-clear-recent" onClick={clearRecentSearches}>
                          <Icon source={DeleteIcon} />
                          Clear
                        </button>
                      </div>
                      {recentSearches.map((term) => (
                        <button
                          key={term}
                          className="search-dropdown-item search-dropdown-recent"
                          onClick={() => handleRecentClick(term)}
                        >
                          <Icon source={ClockIcon} />
                          <span>{term}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right: Icons */}
          <div className="header-right">
            <button
              className="header-icon-btn header-icon-btn--search-mobile"
              onClick={onSearchOpen}
              aria-label="Search"
            >
              <Icon source={SearchIcon} />
            </button>
            <button
              className="header-icon-btn header-icon-btn--desktop"
              onClick={() => navigate('/shortlist')}
              aria-label="Shortlist"
            >
              <Icon source={HeartIcon} />
              {shortlistCount > 0 && (
                <span className="header-badge">{shortlistCount}</span>
              )}
            </button>
            <button
              className="header-icon-btn header-icon-btn--desktop"
              onClick={() => navigate('/inquiry-basket')}
              aria-label="Inquiry Basket"
            >
              <Icon source={CartIcon} />
              {inquiryBasketCount > 0 && (
                <span className="header-badge">{inquiryBasketCount}</span>
              )}
            </button>
            <div className="header-icon-btn--desktop">
              <Popover
                active={userMenuOpen}
                activator={userMenuActivator}
                onClose={() => setUserMenuOpen(false)}
                preferredAlignment="right"
              >
                {isAuthenticated ? (
                  <ActionList
                    items={[
                      {
                        content: user?.name || 'User',
                        helpText: user?.email || user?.phone || '',
                        disabled: true,
                      },
                      {
                        content: 'My Inquiries',
                        onAction: () => {
                          navigate('/inquiries');
                          setUserMenuOpen(false);
                        },
                      },
                      {
                        content: 'Log out',
                        icon: ExitIcon,
                        destructive: true,
                        onAction: handleLogout,
                      },
                    ]}
                  />
                ) : (
                  <ActionList
                    items={[
                      {
                        content: 'Track Inquiry',
                        onAction: () => {
                          navigate('/track-inquiry');
                          setUserMenuOpen(false);
                        },
                      },
                    ]}
                  />
                )}
              </Popover>
            </div>
          </div>
        </div>
      </header>
    );
  }
);
